import { useEffect, useMemo, useState } from 'react';
import { Plus, ShoppingCart } from 'lucide-react';
import { CartCard } from './sections/CartCard';
import { useDispatch, useSelector } from 'react-redux';
import { useGetCart } from '~/api';
import { dropSettledQuantityOverrides } from '~/redux/reducers/cart';
import type { RootState } from '~/redux/store';
import { useCartsWithQuantityOverrides } from '~/hooks/useQuantityOverrides';
import { Button } from '~/components/ui/buttons/Button';
import { EmptyState } from '~/components/ui/EmptyState';
import { PageLoadingSpinner } from '~/components/ui/spinners/PageLoadingSpinner';
import { useTranslation } from 'react-i18next';
import type { Cart } from '~/graphql/graphql';
import { isBuyerCapable, sellerLabel } from '~/utils';
import { NewCartModal } from './sections/new-cart/NewCartModal';

export default function Cart() {
  // Deliberately unfiltered: filtering by the browsed shipping location hides the
  // buyer's carts at every other location.
  const { data, isPending } = useGetCart(undefined, { withContacts: true });
  const serverCarts = useMemo(
    () => (data?.carts ?? []) as Cart[],
    [data?.carts]
  );
  const carts = useCartsWithQuantityOverrides(serverCarts);

  // Nothing mirrors this page's carts into Redux, so the settled edits have to be
  // handed back to server data here instead. Re-runs on `quantityOverrides` too —
  // a settle dispatch can land after the refetch it triggered.
  const dispatch = useDispatch();
  const quantityOverrides = useSelector(
    (state: RootState) => state.cart.quantityOverrides
  );
  useEffect(() => {
    if (data?.carts) dispatch(dropSettledQuantityOverrides());
  }, [data?.carts, quantityOverrides, dispatch]);

  const suppliersCount = carts.length;
  const itemsCount = carts.reduce(
    (acc, cart) => acc + (cart.items?.length || 0),
    0
  );

  const { t } = useTranslation('cart');
  const isBuyer = useSelector((state: RootState) =>
    isBuyerCapable(state.auth.user?.role)
  );
  const [isNewCartOpen, setIsNewCartOpen] = useState(false);
  const newCartButton = (testId: string) => (
    <Button
      variant="primary"
      size="sm"
      width="auto"
      onClick={() => setIsNewCartOpen(true)}
      data-testid={testId}
    >
      <Plus size={14} aria-hidden="true" />
      {t('cart.new_cart.action')}
    </Button>
  );

  return (
    <div className="flex flex-col gap-6">
      <title>{t('cart.title')}</title>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl text-neut-prim mb-2">
            {t('cart.title')}
          </h1>
          <p className="text-sm text-neut-sec">
            {t('cart.count_message', { itemsCount, suppliersCount })}
          </p>
        </div>
        {isBuyer && newCartButton('cart-new-cart')}
      </div>
      {isNewCartOpen && (
        <NewCartModal onClose={() => setIsNewCartOpen(false)} />
      )}

      {isPending ? (
        <PageLoadingSpinner name={t('cart.title')} />
      ) : itemsCount === 0 ? (
        <>
          <EmptyState
            icon={<ShoppingCart size={48} />}
            title={t('cart.empty_cart_title')}
            message={t('cart.empty_cart_description')}
          />
          {isBuyer && (
            <div className="flex justify-center">
              {newCartButton('cart-empty-new-cart')}
            </div>
          )}
        </>
      ) : (
        <div className="flex flex-col gap-8" data-testid="cart-list">
          {carts.map((cart: Cart) => (
            <CartCard
              key={cart.id}
              supplierName={sellerLabel(cart.sellingOrganization)}
              organizationId={cart.sellingOrganization.id}
              items={cart?.items ?? []}
              id={cart.id}
              parentOrderId={cart.parentOrder?.id}
              shippingLocation={cart.shippingLocation}
              sellerOrganizationFacility={cart.sellerOrganizationFacility}
              incoTerm={cart.incoTerm}
              transportVehicle={cart.transportVehicle}
              isInternational={cart.isInternational}
              isFromQuote={cart.isFromQuote}
              incotermDischarge={cart.incotermDischarge}
              incotermAddressDestination={cart.incotermAddressDestination}
              deliveryAddress={cart.deliveryAddress}
              deliveryMethod={cart.deliveryMethod}
              portOfLoading={cart.portOfLoading}
              portOfDischarge={cart.portOfDischarge}
              shippingRoute={cart.shippingRoute}
              tarp={cart.tarp}
              currency={cart.currency}
              contacts={cart.sellerContacts}
            />
          ))}
        </div>
      )}
    </div>
  );
}
